import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import TimerIcon from '@mui/icons-material/Timer';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../axios';
import Header from '../components/Header';
import ContextNavBar from '../components/ContextNavBar';
import ModuleHeader from '../components/ModuleHeader';
import Footer from '../components/Footer';

function getTipoFromToken() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(decodeURIComponent(atob(base64).split('').map(c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join('')));
    return payload?.tipo || null;
  } catch {
    return null;
  }
}

const AreaConsolidador = ({ onLogout }) => {
  const [visitantes, setVisitantes] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    nao_contactado: 0,
    contatado: 0,
    atrasados: 0
  });
  const [loading, setLoading] = useState(true);
  const [dialogContato, setDialogContato] = useState(false);
  const [dialogLiberar, setDialogLiberar] = useState(false);
  const [dialogPerfil, setDialogPerfil] = useState(false);
  const [dialogEditarVisitante, setDialogEditarVisitante] = useState(false);
  const [dialogExcluirVisitante, setDialogExcluirVisitante] = useState(false);
  const [dialogCadastrar, setDialogCadastrar] = useState(false);
  const [dialogCiclo, setDialogCiclo] = useState(false);
  const [visitanteSelecionado, setVisitanteSelecionado] = useState(null);
  const [editForm, setEditForm] = useState({ nome: '', telefone: '', email: '' });
  const [relatorioContato, setRelatorioContato] = useState('');
  const [statusContato, setStatusContato] = useState('primeiro_contato');
  const [desfechoCiclo, setDesfechoCiclo] = useState('negativa_continuar');
  const [novoForm, setNovoForm] = useState({
    nome: '', sexo: '', telefone: '', email: '',
    culto: '', data_presenca: '', observacoes: '',
    quer_ser_membro: false, deseja_oracao: false, quer_conhecer_jesus: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [consolidadorId, setConsolidadorId] = useState(null);
  const [consolidadorData, setConsolidadorData] = useState(null);
  const [perfilForm, setPerfilForm] = useState({
    nome: '',
    telefone: '',
    telefone_whatsapp: '',
    sexo: '',
    endereco: '',
    bairro: '',
    cep: '',
    cidade: '',
    estado: '',
    disponivel_contato: true
  });

  useEffect(() => {
    const tipo = getTipoFromToken();
    if (tipo === 'consolidador') {
      fetchConsolidadorData();
      fetchMeuPerfil();
    } else {
      // admin ou outro papel com acesso: busca visitantes sem perfil pessoal
      fetchVisitantesAdmin();
      setLoading(false);
    }
  }, []);

  const fetchMeuPerfil = async () => {
    try {
      const response = await api.get('/consolidadores/perfil/me');
      setConsolidadorData(response.data);
      setConsolidadorId(response.data.id);
      
      setPerfilForm({
        nome: response.data.nome || '',
        telefone: response.data.telefone || '',
        telefone_whatsapp: response.data.telefone_whatsapp || '',
        sexo: response.data.sexo || '',
        endereco: response.data.endereco || '',
        bairro: response.data.bairro || '',
        cep: response.data.cep || '',
        cidade: response.data.cidade || '',
        estado: response.data.estado || '',
        disponivel_contato: response.data.disponivel_contato !== false
      });
      
      // Buscar visitantes após ter o ID
      await fetchVisitantes(response.data.id);
    } catch (error) {
      console.error('Erro ao buscar perfil:', error);
      setError('Erro ao carregar perfil do consolidador');
    } finally {
      setLoading(false);
    }
  };

  const fetchConsolidadorData = async () => {
    // Esta função agora é chamada por fetchMeuPerfil
    // Mantida para compatibilidade mas não faz nada
  };

  const fetchVisitantesAdmin = async () => {
    try {
      const response = await api.get('/visitantes/');
      const data = Array.isArray(response.data) ? response.data : [];
      setVisitantes(data);
      const total = data.length;
      const nao_contactado = data.filter(v => !getContatado(v)).length;
      const contatado = data.filter(v => getContatado(v)).length;
      const atrasados = data.filter(v => getPrazoExpirado(v) && !getContatado(v)).length;
      setStats({ total, nao_contactado, contatado, atrasados });
    } catch (error) {
      console.error('Erro ao buscar visitantes (admin):', error);
    }
  };

  const getVisitanteNome = (v) => v ? (v.visitante_nome || v.nome || '') : '';
  const getVisitanteTel = (v) => v ? (v.visitante_telefone || v.telefone_celular || '') : '';
  const getVisitanteEmail = (v) => v ? (v.visitante_email || v.email || '') : '';
  const getDataLimite = (v) => v ? (v.data_limite || '') : '';
  const getStatusContato = (v) => v ? (v.status_contato || '') : '';
  const getContatado = (v) => v ? (v.contatado ?? v.distribuido ?? false) : false;
  const getPrazoExpirado = (v) => v ? (v.prazo_expirado ?? false) : false;

  const fetchVisitantes = async (consId) => {
    try {
      const response = await api.get(`/distribuicoes/consolidador/${consId}`);
      setVisitantes(response.data);

      // Calcular estatísticas
      const total = response.data.length;
      const nao_contactado = response.data.filter(v => !getContatado(v)).length;
      const contatado = response.data.filter(v => getContatado(v)).length;
      const atrasados = response.data.filter(v => getPrazoExpirado(v) && !getContatado(v)).length;

      setStats({ total, nao_contactado, contatado, atrasados });
    } catch (error) {
      console.error('Erro ao buscar visitantes:', error);
      setError('Erro ao carregar visitantes');
    }
  };

  const handleAbrirDialogContato = (visitante) => {
    setVisitanteSelecionado(visitante);
    setDialogContato(true);
    setRelatorioContato('');
    setStatusContato('primeiro_contato');
  };

  const handleRegistrarContato = async () => {
    if (!relatorioContato.trim()) {
      setError('Digite um relatório do contato');
      return;
    }

    try {
      await api.post(
        `/distribuicoes/${visitanteSelecionado.distribuicao_id}/registrar-contato`,
        null,
        {
          params: {
            relatorio: relatorioContato,
            status_visitante: statusContato
          }
        }
      );

      setSuccess('Contato registrado com sucesso!');
      setDialogContato(false);
      fetchVisitantes(consolidadorId);
    } catch (error) {
      const det = error.response?.data?.detail;
      setError(Array.isArray(det) ? det.map(e => e.msg).join('; ') : det || 'Erro ao registrar contato');
    }
  };

  const handleAbrirDialogLiberar = (visitante) => {
    setVisitanteSelecionado(visitante);
    setDialogLiberar(true);
  };

  const handleLiberarVisitante = async () => {
    try {
      await api.post(`/distribuicoes/${visitanteSelecionado.distribuicao_id}/liberar`);
      
      setSuccess('Visitante liberado e redistribuído');
      setDialogLiberar(false);
      fetchVisitantes(consolidadorId);
    } catch (error) {
      const det = error.response?.data?.detail;
      setError(Array.isArray(det) ? det.map(e => e.msg).join('; ') : det || 'Erro ao liberar visitante');
    }
  };

  const getVisitanteId = (v) => v.visitante_id || v.id;

  const handleAbrirDialogEditar = (visitante) => {
    setVisitanteSelecionado(visitante);
    setEditForm({
      nome: getVisitanteNome(visitante),
      telefone: getVisitanteTel(visitante),
      email: getVisitanteEmail(visitante),
    });
    setDialogEditarVisitante(true);
  };

  const handleSalvarEdicao = async () => {
    const id = getVisitanteId(visitanteSelecionado);
    try {
      await api.put(`/visitantes/${id}`, editForm);
      setSuccess('Visitante atualizado com sucesso!');
      setDialogEditarVisitante(false);
      if (consolidadorId) fetchVisitantes(consolidadorId);
      else fetchVisitantesAdmin();
    } catch (error) {
      const det = error.response?.data?.detail;
      setError(Array.isArray(det) ? det.map(e => e.msg).join('; ') : det || 'Erro ao editar visitante');
    }
  };

  const handleAbrirDialogExcluir = (visitante) => {
    setVisitanteSelecionado(visitante);
    setDialogExcluirVisitante(true);
  };

  const handleConfirmarExclusao = async () => {
    const id = getVisitanteId(visitanteSelecionado);
    try {
      await api.delete(`/visitantes/${id}`);
      setSuccess('Visitante excluído com sucesso!');
      setDialogExcluirVisitante(false);
      if (consolidadorId) fetchVisitantes(consolidadorId);
      else fetchVisitantesAdmin();
    } catch (error) {
      const det = error.response?.data?.detail;
      setError(Array.isArray(det) ? det.map(e => e.msg).join('; ') : det || 'Erro ao excluir visitante');
    }
  };

  const handleAbrirDialogPerfil = () => {
    setDialogPerfil(true);
  };

  // ---- Status por cores (vermelho/branco/amarelo/verde) ----
  const corParaMui = {
    verde: 'success',
    amarelo: 'warning',
    vermelho: 'error',
    branco: 'default'
  };
  const corLabel = {
    verde: 'Verde',
    amarelo: 'Amarelo',
    vermelho: 'Vermelho',
    branco: 'Branco'
  };
  const getCorVisitante = (v) => (v && v.cor) || 'branco';

  // ---- Cadastrar visitante (responsabilidade do consolidador) ----
  const handleChangeNovo = (e) => {
    const { name, value, checked } = e.target;
    setNovoForm((prev) => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value
    }));
  };

  const handleCadastrarVisitante = async () => {
    if (!novoForm.nome.trim()) {
      setError('Informe o nome do visitante');
      return;
    }
    try {
      await api.post('/visitantes/', novoForm);
      setSuccess('Visitante cadastrado e agora está sob sua responsabilidade!');
      setDialogCadastrar(false);
      setNovoForm({
        nome: '', sexo: '', telefone: '', email: '',
        culto: '', data_presenca: '', observacoes: '',
        quer_ser_membro: false, deseja_oracao: false, quer_conhecer_jesus: false
      });
      if (consolidadorId) fetchVisitantes(consolidadorId);
    } catch (error) {
      const det = error.response?.data?.detail;
      setError(Array.isArray(det) ? det.map(e => e.msg).join('; ') : det || 'Erro ao cadastrar visitante');
    }
  };

  // ---- Marcar ciclo completo (negativa_continuar | tornou_membro) ----
  const handleAbrirDialogCiclo = (visitante) => {
    setVisitanteSelecionado(visitante);
    setDesfechoCiclo('negativa_continuar');
    setDialogCiclo(true);
  };

  const handleMarcarCiclo = async () => {
    const id = getVisitanteId(visitanteSelecionado);
    try {
      await api.post(`/visitantes/${id}/ciclo-completo`, null, {
        params: { desfecho: desfechoCiclo }
      });
      setSuccess('Ciclo do visitante marcado como completo!');
      setDialogCiclo(false);
      if (consolidadorId) fetchVisitantes(consolidadorId);
      else fetchVisitantesAdmin();
    } catch (error) {
      const det = error.response?.data?.detail;
      setError(Array.isArray(det) ? det.map(e => e.msg).join('; ') : det || 'Erro ao marcar ciclo');
    }
  };

  const handleSalvarPerfil = async () => {
    try {
      const params = new URLSearchParams();
      Object.keys(perfilForm).forEach(key => {
        if (perfilForm[key] !== null && perfilForm[key] !== '') {
          params.append(key, perfilForm[key]);
        }
      });

      await api.put(`/consolidadores/perfil/me?${params.toString()}`);
      
      setSuccess('Perfil atualizado com sucesso!');
      setDialogPerfil(false);
      fetchMeuPerfil();
    } catch (error) {
      const det = error.response?.data?.detail;
      setError(Array.isArray(det) ? det.map(e => e.msg).join('; ') : det || 'Erro ao atualizar perfil');
    }
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);
    return data.toLocaleString('pt-BR');
  };

  const getTempoRestante = (dataLimite) => {
    const agora = new Date();
    const limite = new Date(dataLimite);
    const diff = limite - agora;
    
    if (diff <= 0) return 'Prazo vencido';
    
    const horas = Math.floor(diff / (1000 * 60 * 60));
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${horas}h ${minutos}min`;
  };

  const statusOptions = [
    { value: 'nao_contactado', label: 'Não Contactado' },
    { value: 'primeiro_contato', label: 'Primeiro Contato' },
    { value: 'segundo_contato', label: 'Segundo Contato' },
    { value: 'deseja_visita', label: 'Deseja Visita' },
    { value: 'deseja_oracao', label: 'Deseja Oração' },
    { value: 'pretende_ficar', label: 'Pretende Ficar' },
    { value: 'nao_encontrado', label: 'Não Encontrado' }
  ];

  return (
    <>
      <Header onLogout={onLogout} />
      <ContextNavBar context="consolidacao" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
          <Typography sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
            Área do Consolidador
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setDialogCadastrar(true)}
              disabled={!consolidadorId}
            >
              Cadastrar Visitante
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleAbrirDialogPerfil}
            >
              Editar Perfil
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Card de Perfil */}
        {consolidadorData && (
          <Card sx={{ mb: 4, bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    {consolidadorData.nome}
                  </Typography>
                  <Typography variant="body2">
                    <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {consolidadorData.email}
                  </Typography>
                  {consolidadorData.telefone_whatsapp && (
                    <Typography variant="body2">
                      <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      {consolidadorData.telefone_whatsapp}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
                    <Chip
                      label={consolidadorData.sexo === 'M' || consolidadorData.sexo === 'masculino' ? 'Masculino' : 'Feminino'}
                      color="secondary"
                      size="small"
                    />
                    <Chip
                      label={consolidadorData.disponivel_contato ? 'Disponível' : 'Indisponível'}
                      color={consolidadorData.disponivel_contato ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Cards de Estatísticas */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>{stats.total}</Typography>
                  </Box>
                  <PersonAddIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Pendentes
                    </Typography>
                    <Typography variant="h4" color="warning.main" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>
                      {stats.nao_contactado}
                    </Typography>
                  </Box>
                  <TimerIcon sx={{ fontSize: 48, color: 'warning.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Contatados
                    </Typography>
                    <Typography variant="h4" color="success.main" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>
                      {stats.contatado}
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Atrasados
                    </Typography>
                    <Typography variant="h4" color="error.main" sx={{ fontSize: '4.4rem', lineHeight: 1 }}>
                      {stats.atrasados}
                    </Typography>
                  </Box>
                  <WarningIcon sx={{ fontSize: 48, color: 'error.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabela de Visitantes */}
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Contato</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Prazo</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Distribuição</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }} align="center">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitantes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                      Nenhum visitante atribuído no momento
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visitantes.map((visitante, idx) => (
                  <TableRow
                    key={visitante.distribuicao_id ?? visitante.id ?? idx}
                    hover
                    sx={{
                      bgcolor: {
                        vermelho: 'error.lighter',
                        amarelo: 'warning.lighter',
                        verde: 'success.lighter',
                        branco: 'inherit'
                      }[getCorVisitante(visitante)]
                    }}
                  >
                    <TableCell>
                      <strong>{getVisitanteNome(visitante)}</strong>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        {getVisitanteTel(visitante) && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {getVisitanteTel(visitante)}
                            </Typography>
                          </Box>
                        )}
                        {getVisitanteEmail(visitante) && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {getVisitanteEmail(visitante)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          statusOptions.find(s => s.value === getStatusContato(visitante))?.label ||
                          getStatusContato(visitante)
                        }
                        size="small"
                        color={getContatado(visitante) ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Limite: ${formatarData(getDataLimite(visitante))}`}>
                        <Chip
                          label={getDataLimite(visitante) ? getTempoRestante(getDataLimite(visitante)) : 'Sem prazo'}
                          size="small"
                          color={getPrazoExpirado(visitante) ? 'error' : 'default'}
                          icon={getPrazoExpirado(visitante) ? <WarningIcon /> : <TimerIcon />}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatarData(visitante.data_distribuicao)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
                        {visitante.distribuicao_id && !getContatado(visitante) ? (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => handleAbrirDialogContato(visitante)}
                            >
                              Registrar Contato
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleAbrirDialogLiberar(visitante)}
                            >
                              Liberar
                            </Button>
                          </>
                        ) : visitante.distribuicao_id && getContatado(visitante) ? (
                          <Chip label="Contatado" color="success" size="small" />
                        ) : (
                          <Chip label="Sem distribuição" size="small" variant="outlined" />
                        )}
                        {!visitante.ciclo_completo ? (
                          <Button
                            size="small"
                            variant="text"
                            color="success"
                            onClick={() => handleAbrirDialogCiclo(visitante)}
                            title="Marcar ciclo completo"
                          >
                            Ciclo
                          </Button>
                        ) : (
                          <Chip
                            label={visitante.desfecho === 'tornou_membro' ? 'Membro ✓' : 'Negativa'}
                            color={visitante.desfecho === 'tornou_membro' ? 'success' : 'default'}
                            size="small"
                          />
                        )}
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleAbrirDialogEditar(visitante)}
                          title="Editar visitante"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleAbrirDialogExcluir(visitante)}
                          title="Excluir visitante"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog Registrar Contato */}
        <Dialog open={dialogContato} onClose={() => setDialogContato(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Registrar Contato</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2" gutterBottom>
              Visitante: <strong>{getVisitanteNome(visitanteSelecionado)}</strong>
            </Typography>

            <TextField
              fullWidth
              select
              label="Novo Status"
              value={statusContato}
              onChange={(e) => setStatusContato(e.target.value)}
              sx={{ mt: 2, mb: 2 }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Relatório do Contato"
              value={relatorioContato}
              onChange={(e) => setRelatorioContato(e.target.value)}
              placeholder="Descreva como foi o contato, o que foi conversado, próximos passos..."
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogContato(false)}>Cancelar</Button>
            <Button onClick={handleRegistrarContato} variant="contained">
              Salvar Contato
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Liberar Visitante */}
        <Dialog open={dialogLiberar} onClose={() => setDialogLiberar(false)} fullWidth maxWidth="sm">
          <DialogTitle>Liberar Visitante</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja liberar <strong>{getVisitanteNome(visitanteSelecionado)}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              O visitante será redistribuído para outro consolidador disponível.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogLiberar(false)}>Cancelar</Button>
            <Button onClick={handleLiberarVisitante} variant="contained" color="warning">
              Confirmar Liberação
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Editar Visitante */}
        <Dialog open={dialogEditarVisitante} onClose={() => setDialogEditarVisitante(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Editar Visitante</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Nome"
              value={editForm.nome}
              onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
              sx={{ mt: 2, mb: 2 }}
            />
            <TextField
              fullWidth
              label="Telefone"
              value={editForm.telefone}
              onChange={(e) => setEditForm({ ...editForm, telefone: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogEditarVisitante(false)}>Cancelar</Button>
            <Button onClick={handleSalvarEdicao} variant="contained">Salvar</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Excluir Visitante */}
        <Dialog open={dialogExcluirVisitante} onClose={() => setDialogExcluirVisitante(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Excluir Visitante</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja excluir <strong>{getVisitanteNome(visitanteSelecionado)}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Esta ação não pode ser desfeita.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogExcluirVisitante(false)}>Cancelar</Button>
            <Button onClick={handleConfirmarExclusao} variant="contained" color="error">Excluir</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Editar Perfil */}
        <Dialog open={dialogPerfil} onClose={() => setDialogPerfil(false)} maxWidth="md" fullWidth>
          <DialogTitle>Editar Perfil</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome Completo"
                  value={perfilForm.nome}
                  onChange={(e) => setPerfilForm({ ...perfilForm, nome: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  value={perfilForm.telefone}
                  onChange={(e) => setPerfilForm({ ...perfilForm, telefone: e.target.value })}
                  placeholder="(11) 98765-4321"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="WhatsApp"
                  value={perfilForm.telefone_whatsapp}
                  onChange={(e) => setPerfilForm({ ...perfilForm, telefone_whatsapp: e.target.value })}
                  placeholder="(11) 98765-4321"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Sexo"
                  value={perfilForm.sexo}
                  onChange={(e) => setPerfilForm({ ...perfilForm, sexo: e.target.value })}
                >
                  <MenuItem value="M">Masculino</MenuItem>
                  <MenuItem value="F">Feminino</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Disponível para Contatos"
                  value={perfilForm.disponivel_contato}
                  onChange={(e) => setPerfilForm({ ...perfilForm, disponivel_contato: e.target.value === 'true' })}
                >
                  <MenuItem value="true">Sim, estou disponível</MenuItem>
                  <MenuItem value="false">Não, estou indisponível</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Endereço"
                  value={perfilForm.endereco}
                  onChange={(e) => setPerfilForm({ ...perfilForm, endereco: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bairro"
                  value={perfilForm.bairro}
                  onChange={(e) => setPerfilForm({ ...perfilForm, bairro: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CEP"
                  value={perfilForm.cep}
                  onChange={(e) => setPerfilForm({ ...perfilForm, cep: e.target.value })}
                  placeholder="12345-678"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cidade"
                  value={perfilForm.cidade}
                  onChange={(e) => setPerfilForm({ ...perfilForm, cidade: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estado"
                  value={perfilForm.estado}
                  onChange={(e) => setPerfilForm({ ...perfilForm, estado: e.target.value })}
                  placeholder="SP"
                  inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogPerfil(false)}>Cancelar</Button>
            <Button onClick={handleSalvarPerfil} variant="contained">
              Salvar Alterações
            </Button>
          </DialogActions>
        </Dialog>
        {/* Dialog Cadastrar Visitante */}
        <Dialog open={dialogCadastrar} onClose={() => setDialogCadastrar(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Cadastrar Visitante</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              O visitante cadastrado ficará sob sua responsabilidade. Atenda os pendentes antes de cadastrar novos.
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField fullWidth required label="Nome Completo" name="nome"
                  value={novoForm.nome} onChange={handleChangeNovo} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Sexo" name="sexo"
                  value={novoForm.sexo} onChange={handleChangeNovo}>
                  <MenuItem value="masculino">Masculino</MenuItem>
                  <MenuItem value="feminino">Feminino</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Telefone" name="telefone"
                  value={novoForm.telefone} onChange={handleChangeNovo} placeholder="(00) 00000-0000" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="E-mail" name="email" type="email"
                  value={novoForm.email} onChange={handleChangeNovo} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Esteve presente na:" name="culto"
                  value={novoForm.culto} onChange={handleChangeNovo}>
                  <MenuItem value="domingo_manha">Culto de Domingo (Manhã)</MenuItem>
                  <MenuItem value="domingo_noite">Culto de Domingo (Noite)</MenuItem>
                  <MenuItem value="fe">Culto Fé</MenuItem>
                  <MenuItem value="shine">Culto Shine</MenuItem>
                  <MenuItem value="rocket">Culto Rocket</MenuItem>
                  <MenuItem value="legacy">Culto Legacy</MenuItem>
                  <MenuItem value="outros">Outros</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Data de presença" name="data_presenca" type="date"
                  value={novoForm.data_presenca} onChange={handleChangeNovo}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Observações" name="observacoes"
                  value={novoForm.observacoes} onChange={handleChangeNovo} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel control={<Checkbox checked={novoForm.quer_ser_membro}
                  onChange={handleChangeNovo} name="quer_ser_membro" />} label="Quer ser membro" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel control={<Checkbox checked={novoForm.deseja_oracao}
                  onChange={handleChangeNovo} name="deseja_oracao" />} label="Deseja oração" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel control={<Checkbox checked={novoForm.quer_conhecer_jesus}
                  onChange={handleChangeNovo} name="quer_conhecer_jesus" />} label="Quer conhecer Jesus" />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogCadastrar(false)}>Cancelar</Button>
            <Button onClick={handleCadastrarVisitante} variant="contained" startIcon={<PersonAddIcon />}>
              Cadastrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Marcar Ciclo Completo */}
        <Dialog open={dialogCiclo} onClose={() => setDialogCiclo(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Marcar Ciclo Completo</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2" gutterBottom>
              Visitante: <strong>{getVisitanteNome(visitanteSelecionado)}</strong>
            </Typography>
            <TextField fullWidth select label="Desfecho" value={desfechoCiclo}
              onChange={(e) => setDesfechoCiclo(e.target.value)} sx={{ mt: 2 }}>
              <MenuItem value="negativa_continuar">Não quis continuar (negativa)</MenuItem>
              <MenuItem value="tornou_membro">Tornou-se membro</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogCiclo(false)}>Cancelar</Button>
            <Button onClick={handleMarcarCiclo} variant="contained" color="success">
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    <Footer />
    </>
  );
};

export default AreaConsolidador;
